import React from 'react'
import { Col, List, Modal, Radio, Row, Typography } from 'antd'
import { Formik, FormikProps } from 'formik'
import { useTranslation } from 'react-i18next'

import { Input } from 'src/components/Formik'
import { PolicyFlowApproval, PolicyFlowFormat, PolicyFlowType } from 'src/types'

import * as CSS from './styles'
import * as Values from './initial-values'

interface Props {
  disabled?: boolean
  formikProps: FormikProps<PolicyFlowApproval>
  isLoading?: boolean
  isVisible: boolean
  modalPolicyFlowFormat: PolicyFlowFormat
  modalPolicyFlowType: PolicyFlowType
  refFormik: React.MutableRefObject<Formik<PolicyFlowApproval>>
  setModalPolicyFlowFormat?: React.Dispatch<
    React.SetStateAction<PolicyFlowFormat>
  >
  setModalPolicyFlowType?: React.Dispatch<React.SetStateAction<PolicyFlowType>>
}

export default function FlowApprovalsModal({
  disabled = false,
  formikProps,
  isLoading = false,
  isVisible,
  modalPolicyFlowFormat,
  modalPolicyFlowType,
  refFormik,
  setModalPolicyFlowFormat,
  setModalPolicyFlowType
}: Props) {
  const { t } = useTranslation()

  const policyFlowType = [
    {
      description: t('web.policies-flow.approval.type.hierarchy-description'),
      title: t('web.policies-flow.type.hierarchy'),
      value: PolicyFlowType.HIERARCHY
    },
    {
      description: t('web.policies-flow.approval.type.cost-center-description'),
      title: t('web.policies-flow.type.cost-center'),
      value: PolicyFlowType.COST_CENTER
    },
    {
      description: t('web.policies-flow.approval.type.reason-description'),
      title: t('web.policies-flow.type.reason'),
      value: PolicyFlowType.REASON
    }
  ]

  const policyFlowFormat = [
    {
      description: t('web.policies-flow.approval.format.standard-description'),
      title: t('web.policies-flow.format.standard'),
      value: PolicyFlowFormat.STANDARD
    },
    {
      description: t('web.policies-flow.approval.format.automatic-description'),
      title: t('web.policies-flow.format.automatic'),
      value: PolicyFlowFormat.AUTOMATIC
    },
    {
      description: t('web.policies-flow.approval.format.consensus-description'),
      title: t('web.policies-flow.format.consensus'),
      value: PolicyFlowFormat.CONSENSUS
    }
  ]

  const title = t('web.policies-flow.approval.modal-title')

  return (
    <Modal
      centered
      closable
      destroyOnClose
      okButtonProps={{
        loading: isLoading,
        hidden: disabled
      }}
      cancelButtonProps={{
        loading: isLoading
      }}
      onCancel={() => {
        refFormik.current?.resetForm(Values.initialValues)
      }}
      onOk={(e: any) => {
        refFormik.current?.handleSubmit(e)
      }}
      title={title}
      visible={isVisible}
      width={800}
    >
      <Row gutter={24}>
        <Col span={6}>
          <Input
            data-test-id="modal-approval-code"
            disabled={disabled}
            formikProps={formikProps}
            label="web.tree.node.code"
            name="code"
            placeholder="web.tree.node.code-placeholder"
          />
        </Col>
        <Col span={18}>
          <Input
            data-test-id="modal-approval-name"
            disabled={disabled}
            formikProps={formikProps}
            label="web.tree.node.name"
            name="name"
            placeholder="web.tree.node.name-placeholder"
          />
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={24}>
          <Input
            data-test-id="modal-approval-description"
            disabled={disabled}
            formikProps={formikProps}
            label="web.resources.common.description.title"
            name="description"
            placeholder="web.resources.common.description.placeholder"
            required={false}
          />
        </Col>
      </Row>
      <Row gutter={[24, 0]}>
        <Col span={24}>
          <CSS.ModalTitle>
            {t('web.policies-flow.approval.type.title')}
          </CSS.ModalTitle>
        </Col>
      </Row>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Radio.Group
            defaultValue={PolicyFlowType.HIERARCHY}
            disabled={disabled}
            style={{ width: '100%' }}
            value={modalPolicyFlowType}
          >
            <List
              data-test-id="modal-approval-type"
              dataSource={policyFlowType}
              itemLayout="horizontal"
              renderItem={(item: any) => (
                <Row
                  gutter={[2, 2]}
                  onClick={() => {
                    if (!disabled) {
                      setModalPolicyFlowType(item.value)
                      let cost_center_approval = false
                      let reason_approval = false
                      switch (item.value) {
                        case PolicyFlowType.COST_CENTER:
                          cost_center_approval = true
                          reason_approval = false
                          break
                        case PolicyFlowType.REASON:
                          cost_center_approval = false
                          reason_approval = true
                      }

                      formikProps.setFieldValue(
                        'cost_center_approval',
                        cost_center_approval
                      )
                      formikProps.setFieldValue(
                        'reason_approval',
                        reason_approval
                      )
                    }
                  }}
                >
                  <Col span={5}>
                    <Typography.Text strong>{item.title}</Typography.Text>
                  </Col>
                  <Col span={1}>
                    <Radio value={item.value} />
                  </Col>
                  <Col span={18}>
                    <CSS.ModalDescription>
                      {item.description}
                    </CSS.ModalDescription>
                  </Col>
                </Row>
              )}
            />
          </Radio.Group>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={24}>
          <CSS.ModalTitle>
            {t('web.policies-flow.approval.format.title')}
          </CSS.ModalTitle>
        </Col>
      </Row>
      <Row align="middle" gutter={[24, 24]} justify="space-between">
        <Col span={24}>
          <Radio.Group
            defaultValue={PolicyFlowFormat.STANDARD}
            disabled={disabled}
            style={{ width: '100%' }}
            value={modalPolicyFlowFormat}
          >
            <List
              data-test-id="modal-approval-format"
              dataSource={policyFlowFormat}
              itemLayout="horizontal"
              renderItem={(item: any) => (
                <Row
                  gutter={[2, 2]}
                  onClick={() => {
                    if (!disabled) {
                      setModalPolicyFlowFormat(item.value)
                      let automatic_approval = false
                      let consensual_approval = false
                      switch (item.value) {
                        case PolicyFlowFormat.AUTOMATIC:
                          automatic_approval = true
                          consensual_approval = false
                          break
                        case PolicyFlowFormat.CONSENSUS:
                          automatic_approval = false
                          consensual_approval = true
                      }

                      formikProps.setFieldValue(
                        'automatic_approval',
                        automatic_approval
                      )
                      formikProps.setFieldValue(
                        'consensual_approval',
                        consensual_approval
                      )
                    }
                  }}
                >
                  <Col span={5}>
                    <Typography.Text strong>{item.title}</Typography.Text>
                  </Col>
                  <Col span={1}>
                    <Radio value={item.value} />
                  </Col>
                  <Col span={18}>
                    <CSS.ModalDescription>
                      {item.description}
                    </CSS.ModalDescription>
                  </Col>
                </Row>
              )}
            />
          </Radio.Group>
        </Col>
      </Row>
      <Row gutter={[12, 12]}>
        <Col span={24}>
          <CSS.ModalTitle>
            {t('web.policies-flow.approval.exception.title')}
          </CSS.ModalTitle>
        </Col>
      </Row>
      <Row align="middle" gutter={24} justify="space-between">
        <Col span={24}>
          <div
            onClick={() => {
              if (!disabled) {
                formikProps.setFieldValue(
                  'exception_approver',
                  !formikProps.values.exception_approver
                )
              }
            }}
          >
            <CSS.Switch
              checked={formikProps.values.exception_approver}
              data-test-id="modal-approval-exception"
              disabled={disabled}
            />
            <CSS.ModalDescription>
              {t('web.policies-flow.approval.exception.description')}
            </CSS.ModalDescription>
          </div>
        </Col>
      </Row>
    </Modal>
  )
}
